import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import FileUpload from "../components/ui/file-upload";
import { api } from "../lib/utils";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useGetAllCategories } from "../customhooks/useGetAllCategories";
import { useGetAllSubCategories } from "../customhooks/useGetAllSubCategories";

const CreateTicket = () => {
  const navigate = useNavigate();
  // const { user } = useAuth();
  const [formData, setFormData] = useState({
    category_id: "",
    sub_category_id: "",
    subject: "",
    description: "",
    department: "",
    priority: "MEDIUM",
    category: "",
    subcategory: "",
    comment: "",
    status: "OPEN", // Default status for new tickets
  });

  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const categories = useGetAllCategories(formData ? formData?.department : "");
  const subCategories = useGetAllSubCategories(
    formData ? formData?.category : "",
  );

  console.log(formData);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const ticketData = {
        ...formData,
        files: uploadedFiles,
      };

      if (
        !formData?.department ||
        !formData?.category ||
        !formData?.subcategory
      ) {
        return toast.error(
          `Please select ${
            !formData?.department
              ? "department"
              : !formData?.category
              ? "category"
              : "subcategory"
          }`,
        );
      }

      await api.post("/api/tickets", ticketData);
      toast.success("Ticket created successfully!");
      navigate("/my-tickets");
    } catch (error) {
      toast.error("Failed to create ticket");
      console.error("Failed to create ticket:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Reset category and subcategory when department changes
      if (field === "department") {
        newData.category = "";
        newData.subcategory = "";
      } else if (field === "category") {
        newData.subcategory = "";
      }

      return newData;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-4xl px-4">
        <div className="mb-6">
          <h1 className="font-bold" style={{ fontSize: "1.285rem" }}>
            Create New Ticket
          </h1>
          <p className="text-gray-600 mt-2">
            Submit a request to get help with your inquiry
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Ticket Details
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full-width fields */}
            <div className="space-y-4">
              {/* Subject */}
              <div>
                <Label
                  htmlFor="subject"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Subject <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={handleInputChange("subject")}
                  placeholder="Brief description of the issue"
                  required
                  className="w-full"
                />
              </div>

              {/* Description */}
              <div>
                <Label
                  htmlFor="description"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Description <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange("description")}
                  placeholder="Detailed description of the issue"
                  rows={4}
                  required
                  className="w-full resize-none"
                />
              </div>
            </div>

            {/* Two-column grid for dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Priority */}
              <div>
                <Label
                  htmlFor="priority"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Priority
                </Label>
                <Select
                  value={formData.priority}
                  onValueChange={handleInputChange("priority")}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Department */}
              <div>
                <Label
                  htmlFor="department"
                  className="text-sm font-medium text-gray-700 mb-1.5 block"
                >
                  Department <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.department}
                  onValueChange={handleInputChange("department")}
                  required
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Infra</SelectItem>
                    <SelectItem value="FINANCE">Finance</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category - Shows only after Department is selected */}
              {formData.department && (
                <div className="animate-in slide-in-from-top duration-300">
                  <Label
                    htmlFor="category"
                    className="text-sm font-medium text-gray-700 mb-1.5 block"
                  >
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={
                      formData.category_id ? String(formData.category_id) : ""
                    }
                    onValueChange={(id) => {
                      const selected = categories.find(
                        (c) => String(c.id) === String(id),
                      );
                      setFormData((prev) => ({
                        ...prev,
                        category_id: selected?.id || "",
                        category: selected?.category_name || "",
                        subcategory: "",
                      }));
                    }}
                    required
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Category">
                        {
                          categories.find(
                            (c) =>
                              String(c.id) === String(formData.category_id),
                          )?.category_name
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category?.id}>
                          {category?.category_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Subcategory - Shows only after Category is selected */}
              {formData.category && formData.department && (
                <div className="animate-in slide-in-from-top duration-300">
                  <Label
                    htmlFor="subcategory"
                    className="text-sm font-medium text-gray-700 mb-1.5 block"
                  >
                    Subcategory <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={
                      formData.sub_category_id
                        ? String(formData.sub_category_id)
                        : ""
                    }
                    // onValueChange={handleInputChange("subcategory")}
                    onValueChange={(id) => {
                      const selected = subCategories.find(
                        (c) => String(c.id) === String(id),
                      );
                      setFormData((prev) => ({
                        ...prev,
                        sub_category_id: selected?.id || "",
                        subcategory: selected?.sub_category_name,
                      }));
                    }}
                    required
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Subcategory">
                        {
                          subCategories.find(
                            (c) =>
                              String(c.id) === String(formData.sub_category_id),
                          )?.sub_category_name
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {subCategories.map((subcategory) => (
                        <SelectItem key={subcategory} value={subcategory?.id}>
                          {subcategory?.sub_category_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* File Upload Section */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Upload Files
              </Label>
              <FileUpload
                value={uploadedFiles}
                onChange={setUploadedFiles}
                maxFiles={5}
                maxSizeMB={10}
                disabled={loading}
              />
            </div>

            {/* Comment Field */}
            <div>
              <Label
                htmlFor="comment"
                className="text-sm font-medium text-gray-700 mb-1.5 block"
              >
                Comment <span className="text-gray-400">(Optional)</span>
              </Label>
              <Textarea
                id="comment"
                value={formData.comment}
                onChange={handleInputChange("comment")}
                placeholder="Add any additional comments or notes..."
                rows={3}
                className="w-full resize-none"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button type="submit" disabled={loading} className="px-6">
                {loading ? (
                  "Creating..."
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Ticket
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/my-tickets")}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTicket;
